import React from 'react';

import { ExternalLink } from '../../../components';
import ArticleFaq from '../ArticleFaq';
import css from '../articleProse.module.css';

const IMG = '/static/images/blog';
const ARROW = '/static/images/landing/hero-cta-arrow.svg';

// Share of searches on Avento where a canopy size was specified.
const CANOPY_DEMAND_COLUMNS = [
  'Canopy size (sqft)',
  'Share of main canopy searches',
  'Share of complete system searches',
  'Combined',
];
const CANOPY_DEMAND_ROWS = [
  { size: '140 to 159', values: ['33.9%', '13.7%', '26.8%'] },
  { size: '160 to 179', values: ['20.2%', '23.2%', '21.2%'] },
  { size: '180 to 199', values: ['13.2%', '38.9%', '22.2%'] },
  { size: '200 and above', values: ['5.7%', '20.4%', '10.9%'] },
];

// Q&A content; BlogArticlePage builds the FAQPage JSON-LD from this same array
// so the schema text always matches the visible answers.
export const faq = [
  {
    question: 'How many jumps should I have before buying my own skydiving gear?',
    answer:
      'Most jumpers buy their first own rig somewhere between 50 and 100 jumps. The jump number ' +
      'is a proxy though. What actually matters is that your canopy size has somewhat settled, ' +
      'that you are jumping often enough for the fixed costs to make sense, and what the buying ' +
      'and rental options are where you are.',
  },
  {
    question: 'Is it cheaper to buy or rent skydiving gear?',
    answer:
      'Buying, for almost anyone jumping regularly. In our example calculation, using current ' +
      'list prices and typical US rental rates, owning becomes the cheaper option from around 20 ' +
      'jumps a year. At 100 jumps a year owning costs roughly 864 USD against 4,500 USD for ' +
      'renting.',
  },
  {
    question: 'Should my first rig be new or used?',
    answer:
      'Used. Gear loses the largest share of its value in its first years, so buying used means ' +
      'somebody else has already absorbed that drop and you can resell for close to what you ' +
      'paid. You are also unlikely to stay on your first setup long enough to justify a new one.',
  },
  {
    question: 'Can I pack rental gear myself?',
    answer:
      'Usually not. Most dropzones require their own staff to pack rental rigs, because the next ' +
      'person to jump it is not you. That means a pack job cost on every rental jump, and no ' +
      'packing practice for you. Once you own your gear you can pack it yourself and that cost ' +
      'disappears.',
  },
  {
    question: 'What canopy size should I be on before buying my own gear?',
    answer:
      'Ideally 190 or smaller, and 170 makes resale easier again. At 200 sqft and above the used ' +
      'market is thin, only 5.7% of main canopy searches on Avento fall in that range, so you ' +
      'will have a hard time selling it on. The right size for you depends on your weight and ' +
      'your skill, so decide it with your instructor rather than by the resale value alone.',
  },
];

// Body for /blog/when-to-buy-your-own-skydiving-gear.
// Copy is taken verbatim from the source article.
const WhenToBuyYourOwnSkydivingGear = () => (
  <div className={css.prose}>
    <p className={css.lead}>
      When to buy your own skydiving gear is largely a question of money. So I had a rare chance to
      put my university degree in Economics to actual use and ran the numbers. Using current list
      prices for a popular setup and rental rates published by dropzones in the US and Europe,
      owning starts to make long term financial sense from around 20 jumps per year. That is a low
      bar, but money is not on its own the thing that should decide it. This article covers what to
      buy before your first rig, what owning gives you that no rental can, what both options
      actually cost, and the specific reasons to wait a bit longer.
    </p>

    <h2>Key Takeaways</h2>
    <ul>
      <li>
        From a pure cost perspective, owning beats renting from around 20 jumps a year. That is very
        low but not by itself the only aspect that matters.
      </li>
      <li>
        Buy your visual altimeter, helmet and audible before you think about a rig. They are cheap,
        a single long-term investment, and they are the pieces where renting is especially annoying.
      </li>
      <li>
        Buy your first rig used. Gear loses most of its value early, and you are unlikely to stay on
        your first setup for long.
      </li>
      <li>
        Avoid buying gear at 200 sqft and above if you can. Only 5.7% of main canopy searches on
        Avento are for 200 sqft and up, so reselling it later is slow and painful. From 190 down it
        gets much easier.
      </li>
      <li>
        Some of the strongest reasons to own are not financial. A harness that fits, a freefly
        friendly container, brake lines set to your arm length, and the same canopy on every jump so
        you can actually learn to fly it.
      </li>
      <li>
        Most jumpers buy their first own rig somewhere between 50 and 100 jumps, but the number
        itself is not the point. Your canopy size, your rental options and the deals available to
        you are.
      </li>
    </ul>

    <h2>What's Ahead</h2>
    <ul className={css.tocPlain}>
      <li>
        <a href="#accessories-first" className={css.tocLink}>
          1. Buy your accessories first
        </a>
      </li>
      <li>
        <a href="#buy-used" className={css.tocLink}>
          2. Your first rig should be used
        </a>
      </li>
      <li>
        <a href="#why-own" className={css.tocLink}>
          3. What owning gives you that renting cannot
        </a>
      </li>
      <li>
        <a href="#downsides" className={css.tocLink}>
          4. What owning costs you beyond the money
        </a>
      </li>
      <li>
        <a href="#costs" className={css.tocLink}>
          5. What owning and renting actually cost
        </a>
      </li>
      <li>
        <a href="#break-even" className={css.tocLink}>
          6. Break even: around 20 jumps a year
        </a>
      </li>
      <li>
        <a href="#right-time" className={css.tocLink}>
          7. Is it the right time for you?
        </a>
      </li>
      <li>
        <a href="#qa" className={css.tocLink}>
          Q&amp;A
        </a>
      </li>
    </ul>

    <h2 id="accessories-first">1. Buy your accessories first</h2>
    <p>
      Before the rig, buy your visual altimeter, your helmet and an audible. All three are cheap
      compared to a complete system and none of them depend on what canopy you are flying, so they
      are useful from the day you buy them until you stop jumping.
    </p>
    <p>
      The <strong>visual altimeter</strong> is the one to get first. You need it on every single
      jump and renting one means depending on whatever is left in the box on a busy day. The{' '}
      <strong>helmet</strong> matters for fit and for comfort, and a shared rental helmet is gross,
      let's be honest. An <strong>audible</strong> gives you a second layer of altitude awareness,
      and for some disciplines you will need one anyway.
    </p>
    <p>Everything from here on is about the rig itself.</p>

    <h2 id="buy-used">2. Your first rig should be used</h2>
    <p>
      For a first setup, buying used is almost always the better decision. There are three reasons.
    </p>
    <p>
      (1) You lose less money: Skydiving gear does not depreciate in a straight line. It loses the
      largest share of its value in the first years and then flattens out. From what we see in the
      used market, this is much closer to a curve than to a line. If you buy used at a fair price,
      most of that drop has already happened to somebody else, and you can often sell the gear on
      later for close to what you paid.
    </p>
    <p>
      (2) You will not be on that setup for long: You should not rush a downsize, but the common
      advice that there is no reason to ever downsize is, in my opinion, also wrong. Very large
      canopies at very low{' '}
      <a href="/wingloading-calculator" className={css.link}>
        wing loading
      </a>{' '}
      come with their own problems, you are considerably more exposed to wind on them. Getting to a
      sensible wing loading over time is a good thing. That does not mean you should rush to
      downsize, or that downsizing to a sub-100 crossbraced canopy can ever be argued with safety.
      It means your first canopy is often a step and not a destination. Buying that step new is
      likely a waste.
    </p>
    <p>
      (3) A perfect fit matters less right now: Perfect harness fit shows itself most in freefly,
      movement jumps and generally advanced levels, which is (unless you are a tunnel kid) not where
      you are yet. And on the larger sizes you will not feel the difference between fabric that is
      brand new and fabric with a few hundred jumps on it, as long as the canopy is in good
      condition. However, it is still important for safety and comfort that it fits you well, but
      the last 5% of perfect fit are not that relevant.
    </p>
    <p>
      When you get to picking the specific items, our{' '}
      <a href="/harness-sizing" className={css.link}>
        Harness Sizing Guide
      </a>{' '}
      and{' '}
      <a href="/container-sizing" className={css.link}>
        Container Sizing Guide
      </a>{' '}
      walk through how to work out what will fit you.
    </p>

    <h2 id="why-own">3. What owning gives you that renting cannot</h2>
    <p>
      The financial case is only half the picture, and in our experience the other half is at least
      as important for people.
    </p>
    <ul>
      <li>
        <strong>Gear that fits you:</strong> A rental harness is whatever there is available and
        will often not fit that well. With your own rig you can choose one that fits you and your
        body.
      </li>
      <li>
        <strong>The options you want:</strong> Many rental rigs are student gear and not freefly
        friendly, which means you cannot (and definitely should not) use them for freefly or angle
        jumps. On your own rig you can make sure it has the options you need, like a pilot chute
        handle with a freefly tuck tab.
      </li>
      <li>
        <strong>Brake lines set to your body:</strong> Where your flare and your stall point sit
        depends on how long your arms are. On a rental you take what you get and the flare will
        always be different. On your own canopy a rigger can shorten or lengthen the brake lines
        until it is right for you.
      </li>
      <li>
        <strong>You jump on busy days:</strong> With your own gear you don't need to worry about
        rental availabilities on busy days.
      </li>
      <li>
        <strong>You can jump anywhere:</strong> Helicopter and balloon jumps, boogies and events,
        dropzones that have no rental gear at all. With your own rig none of that is a problem.
      </li>
      <li>
        <strong>You learn your canopy:</strong> With rentals you'll constantly jump different
        canopies and even the same model and size flies differently depending on wear and line
        condition. Having the same canopy on the same rig every jump is what lets you get
        comfortable with it and properly progress in flying your wing.
      </li>
      <li>
        <strong>It is just nice to have your own gear:</strong> No calculation needed for that one.
      </li>
    </ul>

    <h2 id="downsides">4. What owning costs you beyond the money</h2>
    <p>Owning is not free of hassle and it is worth being honest about that before you buy.</p>
    <ul>
      <li>
        <strong>Buying takes work:</strong> You have to{' '}
        <a href="/blog/where-to-buy-used-skydiving-gear" className={css.link}>
          find the right item
        </a>
        , check that it fits you, get your rigger to inspect it, arrange pickup or shipping, and
        make sure{' '}
        <a href="/gear-value-calculator" className={css.link}>
          the price is fair
        </a>
        .
      </li>
      <li>
        <strong>Selling carries risk:</strong> When you want to move on, you first need to find
        somebody who wants what you have. Good gear at a fair price is rather easy to sell, but if
        you overpaid at the start, or bought something with little demand, you either sit on it for
        ages or take a loss. This is the main argument for being patient and buying well in the
        first place.
      </li>
      <li>
        <strong>Repairs are yours:</strong> A worn leg pad, a hole to patch, a lineset that is due.
        On your own gear it is also your own responsibility and cost.
      </li>
      <li>
        <strong>Travel gets more complicated:</strong> Your rig comes with you to every dropzone,
        and flying somewhere often means paying for the extra luggage.
      </li>
    </ul>

    <h2 id="costs">5. What owning and renting actually cost</h2>
    <h3>The cost of owning</h3>
    <ul>
      <li>
        <strong>Purchase:</strong> You can get a complete system in good used condition for roughly
        4,000 USD, though this varies a lot depending on what and how used you are looking.
      </li>
      <li>
        <strong>Depreciation:</strong> The part of the purchase price you do not get back when you
        sell. This is the actual cost of owning, not the purchase price itself.
      </li>
      <li>
        <strong>Reserve repacks:</strong> Around 100 USD each. In the US the reserve has to be
        repacked within the preceding 180 days under{' '}
        <ExternalLink
          className={css.link}
          href="https://www.ecfr.gov/current/title-14/chapter-I/subchapter-F/part-105/subpart-B/section-105.43"
        >
          FAR 105.43
        </ExternalLink>
        , so twice a year. In much of Europe it is once every 12 months. The exact interval comes
        from your national rules and from the reserve and container manufacturers, so check what
        applies where you jump.
      </li>
      <li>
        <strong>Linesets:</strong> Roughly 500 USD every 400 jumps or so for a normal beginner to
        intermediate canopy. As a reference,{' '}
        <ExternalLink className={css.link} href="https://www.jyro.com/product/reline-sport/">
          JYRO's own reline service
        </ExternalLink>{' '}
        is listed at 475 USD including inspection, lineset, softlinks and rigging, with shipping on
        top.
      </li>
      <li>
        <strong>AAD:</strong> If you calculate the purchase price and maintenance over the service
        life you land at roughly 80 to 90 USD a year, depending on which exact unit you have.
      </li>
      <li>
        <strong>Third party liability insurance:</strong> Around 100 USD a year, mainly relevant in
        Europe.{' '}
        <ExternalLink className={css.link} href="https://www.swissskydive.org/dax/apps/SSDShop/">
          Swiss Skydive
        </ExternalLink>{' '}
        charges around 76 CHF a year depending on the cover. In the US this is already{' '}
        <ExternalLink className={css.link} href="https://www.uspa.org/insurance">
          included in your USPA membership
        </ExternalLink>
        .
      </li>
      <li>
        <strong>Small repairs and luggage for travel:</strong> Irregular and not large, but not
        zero.
      </li>
    </ul>

    <h3>The cost of renting</h3>
    <ul>
      <li>
        <strong>Rental:</strong> Around 35 USD per jump is a reasonable planning figure, but it
        varies heavily.{' '}
        <ExternalLink className={css.link} href="https://www.skydiveseb.com/experienced-skydivers/">
          Skydive Sebastian
        </ExternalLink>{' '}
        charges 32 USD and{' '}
        <ExternalLink className={css.link} href="https://skydiveempuriabrava.com/en/all-prices/">
          Skydive Empuriabrava
        </ExternalLink>{' '}
        27.95 EUR. Some dropzones include the pack job in that price, for example{' '}
        <ExternalLink
          className={css.link}
          href="https://sanmarcos.skydivespaceland.com/experienced-skydiver-information/"
        >
          Skydive Spaceland San Marcos
        </ExternalLink>{' '}
        at 30 USD plus tax and{' '}
        <ExternalLink className={css.link} href="https://www.skydiveutah.com/experienced-prices">
          Skydive Utah
        </ExternalLink>{' '}
        at 40 USD.
      </li>
      <li>
        <strong>Packing:</strong> Where it is not included, budget around 10 USD per jump.
        Empuriabrava lists a pack job at 9.70 EUR. This is not optional: most dropzones do not let
        you pack rental gear yourself, because somebody else might jump it next. So you pay for
        every pack job, and you get no packing practice out of it.
      </li>
    </ul>

    <h2 id="break-even">6. Break even: around 20 jumps a year</h2>
    <p>
      This is an example calculation with realistic prices. Nevertheless, it will depend on the
      prices and requirements where you are, you can just run the numbers yourself then. Let's call
      your yearly number of jumps "<em>J</em>".
    </p>

    <h3>The assumptions</h3>
    <p>
      We use straight line depreciation based on age only. In reality jump numbers matter a lot,
      especially for canopies and containers, and real depreciation is steeper at the start and
      flatter later. We also used premium models at full list price and service lives on the
      conservative side. All of those choices push the result against owning, so if anything the
      real break even is lower than what comes out here. We are using the US case: repacks every 180
      days, liability insurance already covered by USPA membership. Small repairs and travel luggage
      are left out on the owning side.
    </p>

    <h3>Fixed yearly cost of owning</h3>
    <p>Depreciation using current list prices:</p>
    <ol>
      <li>
        Container: 200 USD
        <span className={css.listNote}>
          <ExternalLink
            className={css.link}
            href="https://uptvector.com/wp-content/uploads/2019/07/Vector3-Order-Form.pdf"
          >
            UPT Vector 3
          </ExternalLink>{' '}
          at around 5,000 USD with the options most people want, over 25 years
        </span>
      </li>
      <li>
        Main canopy: 155 USD
        <span className={css.listNote}>
          <ExternalLink className={css.link} href="https://www.jyro.com/product/safire-4/">
            JYRO Safire 4
          </ExternalLink>{' '}
          3,090 USD, over 20 years
        </span>
      </li>
      <li>
        AAD: 86 USD
        <span className={css.listNote}>
          <ExternalLink
            className={css.link}
            href="https://www.sskinc.com/products/cypres/products/c-mode-cypres-2/"
          >
            Cypres 2 C-Mode
          </ExternalLink>{' '}
          at 1,400 USD minus the{' '}
          <ExternalLink className={css.link} href="https://www.cypres.aero/services/trade-in/">
            70 USD trade in credit
          </ExternalLink>{' '}
          at end of life, over 15.5 years (No mandatory inspection for newer models)
        </span>
      </li>
      <li>
        Reserve: 98 USD
        <span className={css.listNote}>
          <ExternalLink
            className={css.link}
            href="https://www.chutingstar.com/pd-optimum-reserve-parachute-canopy"
          >
            PD Optimum
          </ExternalLink>{' '}
          at 1,960 USD, over 20 years
        </span>
      </li>
    </ol>
    <p>Total depreciation per year:</p>
    <div className={css.mathCard}>539 USD</div>
    <p>Reserve repacks twice a year at 100 USD:</p>
    <div className={css.mathCard}>200 USD</div>
    <p>Lineset replacement 500 USD every 400 jumps:</p>
    <div className={css.mathCard}>J &middot; (500 / 400) USD = J &middot; 1.25 USD</div>
    <p>Total yearly cost of owning:</p>
    <div className={css.mathCard}>
      <span className={css.mathUnderline}>739 USD + J &middot; 1.25 USD</span>
    </div>

    <h3>Yearly cost of renting</h3>
    <p>Rental at 35 USD and pack job at 10 USD:</p>
    <div className={css.mathCard}>
      <span className={css.mathUnderline}>J &middot; 45 USD</span>
    </div>

    <h3>Setting them equal</h3>
    <div className={css.mathCard}>
      <div>539 + 200 + J &middot; (500 / 400) = J &middot; 45</div>
      <div>739 + J &middot; 1.25 = J &middot; 45</div>
      <div>739 = J &middot; 43.75</div>
      <div>
        <span className={css.mathResult}>J = 16.9</span>
      </div>
    </div>
    <p>
      In this example, owning becomes the cheaper option from 17 jumps a year. Rounded up,{' '}
      <strong>
        if you jump around 20 times a year or more, owning your own gear makes long term financial
        sense.
      </strong>{' '}
      That is not the same as saying you should buy at 20 jumps a year, it only means the money has
      stopped being an argument for renting. Your own numbers will differ, mostly with what renting
      costs where you jump.
    </p>
    <figure className={css.figure}>
      <img
        src={`${IMG}/skydiving-gear-buy-vs-rent-break-even.webp`}
        alt="Chart comparing the yearly cost of owning skydiving gear against renting, breaking even at 17 jumps a year"
        width={2400}
        height={1297}
        loading="lazy"
      />
    </figure>

    <h3>What it looks like at 100 jumps a year</h3>
    <div className={css.tableCard}>
      <table className={css.dataTable}>
        <thead>
          <tr>
            <th scope="col" aria-label="Cost position" />
            <th scope="col">Owning</th>
            <th scope="col">Renting</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Depreciation</th>
            <td>539 USD</td>
            <td />
          </tr>
          <tr>
            <th scope="row">Reserve repacks</th>
            <td>200 USD</td>
            <td />
          </tr>
          <tr>
            <th scope="row">Lineset</th>
            <td>125 USD</td>
            <td />
          </tr>
          <tr>
            <th scope="row">Rental</th>
            <td />
            <td>3,500 USD</td>
          </tr>
          <tr>
            <th scope="row">Packing</th>
            <td />
            <td>1,000 USD</td>
          </tr>
          <tr className={css.dataTableTotal}>
            <th scope="row">Total per year</th>
            <td>864 USD</td>
            <td>4,500 USD</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      At 100 jumps a year, owning costs you roughly 3,600 USD less than renting. That is close to
      what a used complete system costs in the first place.
    </p>

    <h2 id="right-time">7. Is it the right time for you?</h2>
    <p>
      Besides the cost calculation, there are many other factors that decide whether you should buy
      your own gear and if now is also the right time.
    </p>
    <p>
      <strong>Can you carry the upfront cost?</strong> If buying your own gear means four months of
      not jumping while you save it up, that might not be worth it during the season. Keep renting
      through the season and save over the winter instead. Gear that stops you from jumping is not
      worth owning.
    </p>
    <p>
      <strong>What canopy size are you on?</strong> This is the one we feel strongest about. Avoid
      buying gear at 200 sqft and above if you can. Almost nobody buys at that size, because most
      jumpers rent until they have downsized, which makes reselling slow and expensive. Our own
      search data shows how lopsided the demand is.
    </p>
    <div className={css.tableCard}>
      <table className={`${css.dataTable} ${css.dataTableCentered}`}>
        <thead>
          <tr>
            {CANOPY_DEMAND_COLUMNS.map(column => (
              <th scope="col" key={column}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CANOPY_DEMAND_ROWS.map(row => (
            <tr key={row.size}>
              <th scope="row">{row.size}</th>
              {row.values.map((value, i) => (
                <td key={CANOPY_DEMAND_COLUMNS[i + 1]}>{value}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className={css.tableNote}>Share of searches on Avento where a canopy size was specified.</p>
    <p>
      Waiting until you are on a 190, and ideally a 170, makes your gear far easier to move on
      later. It also helps that progression slows down at those sizes compared to student canopies,
      so you will stay on the gear longer. This naturally depends on your weight, and with that your
      wing loading a lot, so talk it through with your instructor or rigger.
    </p>
    <p>
      The same logic applies to the container. Buy one that holds up to the size you are actually
      going to jump and not much beyond it. If you are on a 190, look for a rig that holds roughly
      150 to 190 rather than one built for 190 to 230. You keep room to downsize once or twice, and
      you keep it sellable. Our{' '}
      <a href="/container-sizing" className={css.link}>
        Container Sizing Guide
      </a>{' '}
      covers how to read those ranges.
    </p>
    <p>
      <strong>Are you staying in the sport?</strong> If you are not yet sure you will keep jumping
      for a while, renting a bit longer is cheaper than buying and reselling within a few weeks, and
      it saves you the hassle on both ends.
    </p>
    <p>
      <strong>How much will you actually jump?</strong> The fixed costs run whether you jump or not.
      Repacks and much of the depreciation apply even if you only made 2 jumps.
    </p>
    <p>
      <strong>What are your rental options?</strong> Some dropzones have no rental gear at all, some
      have plenty and charge little for it. Take the numbers in section 5 and replace them with what
      you actually pay.
    </p>
    <p>
      <strong>Is there a good deal in front of you right now?</strong> Being ready to buy is not the
      same as buying the first thing you see. If nothing suitable is available at a fair price, keep
      jumping and keep looking.{' '}
      <a href="/s" className={css.link}>
        Set an alert
      </a>{' '}
      for what you need. Rushing is how you overpay, and overpaying is exactly what makes the
      eventual resale painful.
    </p>

    <h3>The middle ground</h3>
    <p>
      You do not have to choose all at once. Some rigging and gear shops rent for a whole season at
      a flat rate, and some will rent you a main canopy on its own. Buying your rig and renting a
      canopy for a season works well if you know you will downsize soon or cannot find the right
      canopy yet. Ask the riggers and gear shops around you what they offer, some do this without
      advertising it.
    </p>

    <h2 id="qa">Q&amp;A</h2>
    <ArticleFaq faq={faq} />

    <div className={css.ctaRow}>
      <a href="/blog" className={css.ctaButton}>
        All Articles
        <img src={ARROW} alt="" className={css.ctaArrow} width={20} height={20} />
      </a>
    </div>
  </div>
);

export default WhenToBuyYourOwnSkydivingGear;
